import * as React from 'react';

import {ImportStatusBanner} from 'shells/dromo/import-status-banner';
import {ProductImportIcon} from 'shells/icon/svg/product-import-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import {ImportIdentifier} from 'nutshell-graphql-types';
import type {ImportStatus as ImportStatusType} from 'nutshell-graphql-types';

import {useGetProductCategories} from '../../../../categories/graphql/hooks/use-get-product-categories';
import {useGetMarkets} from '../../../../core/select/hooks';
import {useGetSessionUser} from '../../../../core/use-get-session-user';

import {DromoImportButton} from '../dromo-import-button';
import {useUploadDromoImportResults} from '../graphql/hooks';
import {useGetDromoImports} from '../graphql/hooks/use-get-dromo-imports';
import {DromoImportsTable} from '../shared/dromo-imports-table';

import {CATEGORY_ID_KEY, DESCRIPTION_KEY} from './consts';
import {
    generateDromoProductFields,
    getDromoProductBulkRowHooks,
    getDromoProductCategorySelectField,
} from './helpers';

export function DromoProductImporter() {
    const [importStatus, setImportStatus] = React.useState<ImportStatusType>('IN_PROGRESS');
    const [showImportStatusBanner, setShowImportStatusBanner] = React.useState<boolean>(false);

    const {uploadDromoImportResults} = useUploadDromoImportResults();
    const {dromoImports, isLoading, errorMessage, refetch} = useGetDromoImports(
        ImportIdentifier.PRODUCT
    );
    const {
        markets,
        isLoading: isMarketsLoading,
        errorMessage: marketsErrorMessage,
    } = useGetMarkets();
    const {user, isLoading: isUserLoading, errorMessage: userErrorMessage} = useGetSessionUser();
    const {
        productCategories,
        isLoading: isCategoriesLoading,
        error: categoriesError,
    } = useGetProductCategories();

    // @ts-expect-error
    let fields = [];
    if (markets) {
        fields = generateDromoProductFields(markets);
    }
    const categorySelectField = getDromoProductCategorySelectField(productCategories);
    if (categorySelectField) {
        // @ts-expect-error
        fields = fields.filter((field) => field.key !== CATEGORY_ID_KEY);

        const descriptionIndex = fields.findIndex((field) => field.key === DESCRIPTION_KEY);
        if (descriptionIndex !== -1) {
            // Insert category select field after desc
            fields.splice(descriptionIndex + 1, 0, categorySelectField);
        } else {
            fields = [...fields, categorySelectField];
        }
    }

    const bulkRowHooks = getDromoProductBulkRowHooks();

    // @ts-expect-error
    const handleResults = (response, metadata) => {
        uploadDromoImportResults({
            importData: JSON.stringify(response),
            dromoId: metadata.id,
            filename: metadata.filename,
            importIdentifier: metadata.importIdentifier,
        }).then(() => {
            setImportStatus('IN_PROGRESS');
            setShowImportStatusBanner(true);
            // fake delay for import
            setTimeout(() => {
                refetch();
            }, 2000);
        });
    };

    React.useEffect(() => {
        if (dromoImports?.currentImport) {
            setImportStatus('IN_PROGRESS');
        } else if (
            dromoImports &&
            dromoImports.currentImport === null &&
            dromoImports.recentCompletedImport
        ) {
            setImportStatus('COMPLETED');
        }

        if (dromoImports?.recentCompletedImport) {
            setShowImportStatusBanner(true);
        }
    }, [dromoImports]);

    return (
        <Stack spacing={32}>
            {showImportStatusBanner && (
                <ImportStatusBanner
                    onRefresh={() => {
                        refetch();
                    }}
                    onViewEntity={() => {
                        window.location.pathname = 'sales/product';
                    }}
                    status={importStatus}
                    type='products'
                    // count={0} // TODO: get count from backend
                />
            )}
            <Stack spacing={16} isHorizontal={true} alignItems='center'>
                <ProductImportIcon size={86} />
                <Stack spacing={8}>
                    <Body size='large' isBold={true} color='dark'>
                        Import your products with just a few clicks
                    </Body>
                    <DromoImportButton
                        fields={fields}
                        columnHooks={[]}
                        bulkRowHooks={bulkRowHooks}
                        importIdentifier='PRODUCT'
                        onImportComplete={handleResults}
                        user={user}
                        isLoading={isUserLoading || isMarketsLoading || isCategoriesLoading}
                        error={userErrorMessage || marketsErrorMessage || categoriesError}
                        variant='primary'
                        styleOverrides={{
                            global: {
                                primaryTextColor: '#211712',
                                secondaryTextColor: '#7a7370',
                                backgroundColor: '#f7f7f7',
                                borderRadius: '4px',
                            },
                            primaryButton: {
                                borderRadius: '3px',
                                backgroundColor: '#3b9eD7',
                                textColor: '#ffffff',
                                hoverBackgroundColor: '#337da8',
                                hoverTextColor: '#ffffff',
                            },
                            secondaryButton: {
                                borderRadius: '3px',
                                backgroundColor: '#ebe8e8',
                                textColor: '#4d4540',
                                hoverBackgroundColor: '#dbd9d9',
                                hoverTextColor: '#211712',
                            },
                            dropzone: {
                                borderColor: '#3b9eD7',
                                borderRadius: 4,
                            },
                            helpText: {
                                textColor: '#337da8',
                                backgroundColor: '#d9edf7',
                            },
                            stepperBar: {
                                currentColor: '#3b9eD7',
                                incompleteColor: '#7a7370',
                            },
                        }}
                    />
                    <Body size='small' color='light'>
                        Supports .csv, .tsv, .txt, etc.
                    </Body>
                </Stack>
            </Stack>
            <DromoImportsTable
                importHistory={(dromoImports && dromoImports.imports) || []}
                isLoading={isLoading}
                errorMessage={errorMessage}
                importType='products'
            />
        </Stack>
    );
}
